// src/client/renderer/vfx/tile/path-preview-line.ts
//
// Plan 16, Task 2.3 — Path preview line pool + resolver.
//
// Visualises the projected dice-roll path as a pulsing additive line.
//
// ── Design notes ────────────────────────────────────────────────────────────
//
// SINGLE-INSTANCE POOL: at most one active path-preview line at a time.
// Calling acquire() while one is active REPLACES the old line — the old emitter
// is released first, then a new one is created and returned.
//
// RESOLVER NOTE: resolvePathPreviewForEvent() returns null for ALL events in
// Plan 16, including dice_roll. Path is NOT present on DiceRollEvent; it is
// supplied externally by the caller (Plan 5.1 wire-up) via manager.spawn(). The
// resolver is a forward-compatibility hook for Plan 19 which will implement
// path-lookup from a node graph. Until Plan 19 lands, callers use:
//
//   manager.spawn('path_preview', { path: [...], position: origin })
//
// DO NOT change the resolver to return non-null values in this file unless
// Plan 19 provides a node-to-world-position lookup and updates this file.
//
// TIMELINE STRATEGY: Each emitter gets a gsap timeline (via registry) that
// handles both the pulse (opacity 0.4↔1.0 over 1.5s loop) and the 4s lifetime
// flag. The pulse is a yoyo tween with repeat: -1. The lifetime callback is
// placed at absolute t=4.0 via tl.call(fn, undefined, 4.0) per spec.
//
// Forward-defense F4: pool.dispose() MUST clean up any active emitter without
// requiring a prior pool.release() call.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PULSE_MIN_OPACITY = 0.4;
const PULSE_MAX_OPACITY = 1.0;
const PULSE_HALF_PERIOD = 0.75; // seconds; full oscillation = 1.5s (yoyo)

const LIFETIME_SECONDS = 4.0;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  material: THREE.LineBasicMaterial;
  geometry: THREE.BufferGeometry;
  finished: boolean;
  elapsed: number; // cumulative dt tracked in pool.update for lifetime guard
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface PathPreviewLinePoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createPathPreviewLinePool({
  scene,
  registry,
}: PathPreviewLinePoolConfig): TileVfxPool {
  // Single-instance: at most one active emitter at a time.
  let activeEmitter: THREE.Object3D | null = null;
  let activeState: EmitterState | null = null;

  let disposed = false;

  // ── internal release helper ────────────────────────────────────────────────

  function releaseActive(): void {
    if (!activeEmitter || !activeState) return;

    activeState.handle.dispose(); // kills gsap timeline, removes from registry
    scene.remove(activeEmitter);
    activeState.material.dispose();
    activeState.geometry.dispose();
    // Mark finished so any stale reference sees isFinished = true
    activeState.finished = true;

    activeEmitter = null;
    activeState = null;
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Single-instance: release the current emitter before creating a new one.
    if (activeEmitter !== null) {
      releaseActive();
    }

    const path = args.path;

    // Null guard: must have at least one point to draw a line.
    if (!path || path.length === 0) return null;

    // Build BufferGeometry from path points.
    const positions = new Float32Array(path.length * 3);
    for (let i = 0; i < path.length; i++) {
      positions[i * 3 + 0] = path[i].x;
      positions[i * 3 + 1] = path[i].y;
      positions[i * 3 + 2] = path[i].z;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Per-emitter material (transparent, additive blending).
    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: PULSE_MIN_OPACITY,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const line = new THREE.Line(geometry, material);

    // Add to scene.
    scene.add(line);

    // Build gsap timeline.
    const handle = registry.create('path_preview');
    const tl = handle.timeline;

    const state: EmitterState = {
      handle,
      material,
      geometry,
      finished: false,
      elapsed: 0,
    };

    // Pulse: animate an opacity proxy so we stay Three.js-friendly.
    const opacityProxy = { value: PULSE_MIN_OPACITY };
    tl.to(
      opacityProxy,
      {
        value: PULSE_MAX_OPACITY,
        duration: PULSE_HALF_PERIOD,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      0,
    );

    // Lifetime callback at absolute 4.0s — use tl.call(fn, undefined, abs).
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME_SECONDS,
    );

    activeEmitter = line;
    activeState = state;

    return line;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // Accumulates dt and returns the current active set (size 0 or 1).
  // The registry.update(dt) call is performed by the manager; this pool only
  // tracks its own elapsed time for the lifetime guard.

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (activeEmitter && activeState) {
      activeState.elapsed += deltaSeconds;
      if (activeState.elapsed >= LIFETIME_SECONDS) {
        activeState.finished = true;
      }
    }
    return activeEmitter ? new Set([activeEmitter]) : new Set<THREE.Object3D>();
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    // If not the active emitter, it was already released — treat as finished.
    if (emitter !== activeEmitter) return true;
    return activeState ? activeState.finished : true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (emitter !== activeEmitter) return; // safe no-op for stale/unknown refs
    releaseActive();
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: cleans up any active emitter without expecting a prior
  // pool.release() call. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    releaseActive();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a game event to path-preview spawn args.
 *
 * Returns null for ALL events in Plan 16, including dice_roll.
 *
 * Rationale: DiceRollEvent carries no path (it has `playerId`, `result`).
 * Callers that have a path use manager.spawn('path_preview', { path: [...] })
 * directly. This resolver is a forward-compatibility hook — Plan 19 will
 * implement the path-lookup logic (node graph → world positions) and update
 * this resolver to return non-null values for dice_roll events.
 *
 * Contract for Task 5.1 wire-up: wire manager.spawn, NOT this resolver.
 */
export function resolvePathPreviewForEvent(
  event: GameEvent,
): { poolId: 'path_preview'; args: TileVfxSpawnArgs } | null {
  // Only dice_roll events are in scope for this resolver (Plan 19 concern).
  if (event.type !== 'dice_roll') return null;

  // Path is not present on DiceRollEvent — return null for now.
  // Plan 19 will supply the path via a node-graph lookup.
  return null;
}
